/**
 * =============================================================================
 * ROASTBREW - MAIN APPLICATION SHELL
 * =============================================================================
 *
 * Root component: authentication, routing, global state.
 *
 * ROUTES:
 * /                → Landing (logged out) or Home (logged in)
 * /verify-email    → Email verification (public)
 * /reset-password  → Password reset (public)
 * /library         → Video library (protected)
 * /courses         → Courses (protected)
 * /calendar        → Calendar (protected)
 * /grade-check     → Grade Check (protected)
 * /drive           → Drive (protected)
 * /video/:id       → Video player (protected)
 * /playlist                  → Playlists home (protected, Pro+)
 * /playlist/:code            → One playlist (protected, Pro+)
 * /playlist/:code/:item      → Playing an item of a playlist (protected, Pro+)
 * /playlist/:code/edit       → Create / edit / remix a playlist (protected, Pro+)
 * /playlists                 → redirects to /playlist (the old address)
 * /subscription    → Manage subscription (protected, standalone)
 * /profile         → Profile & settings (protected, standalone)
 *
 * =============================================================================
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';

// Components
import AuthContext, { useAuth } from './AuthContext';
import { ThemeProvider } from './ThemeContext';
import { PlaybackCapabilityProvider } from './PlaybackCapabilityContext';
import UnsupportedBrowserGate from './UnsupportedBrowserGate';
import LandingPageObs from './LandingPageObs';
import AuthUI from './AuthUI';
import ManageSubscription from './ManageSubscription';
import ProfilePage from './ProfilePage';
import AppLayout from './AppLayout';
import HomePage from './HomePage';
import LibraryPage from './LibraryPage';
import CoursesPage from './CoursesPage';
import CalendarPage from './CalendarPage';
import GradeCheckPage from './GradeCheckPage';
import DrivePage from './DrivePage';
import VideoViewPage from './VideoViewPage';
import VideoViewRouter from './VideoViewRouter';
import SearchResultsPage from './SearchResultsPage';
import PlaylistsHomePage from './PlaylistsHomePage';
import PlaylistPage from './PlaylistPage';
import PlaylistPlayPage from './PlaylistPlayPage';
import PlaylistEditPage from './PlaylistEditPage';
import ToolsPage from './ToolsPage';
import UpgradePrompt from './UpgradePrompt';
import VerifyEmailPage from './VerifyEmailPage';
import ResetPasswordPage from './ResetPasswordPage';

// Utilities
import { createApiClient } from './utils/api';

// Config
import { TIER_COLORS, canUseFeature } from './config';

// Pre-auth theme. Used ONLY by the loading screen below, which early-returns
// above <ThemeProvider> and so has no context to read. Everything after that
// return uses the signed-in tier as before.
import { PublicTheme } from './publicTheme';
import PublicMark from './PublicMark';

// Data — fallback content used until the real fetch is wired, and as a net if it fails.
import CatalogErrorOverlay from './CatalogErrorOverlay';

// Constants
const API_URL = process.env.REACT_APP_API_URL;
const TOKEN_KEY = 'roastbrew_token';
const REDIRECT_KEY = 'roastbrew_redirect_after_login';
const SYNC_WINDOW_MS = 45 * 24 * 60 * 60 * 1000; // 45 days




// =============================================================================
// SCROLL TO TOP — resets scroll on every route change
// =============================================================================

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.history.scrollRestoration = 'manual';
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname]);
  return null;
}


// =============================================================================
// REQUIRE AUTH WRAPPER
// =============================================================================

function RequireAuth({ children }) {
  const { profile, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return null;

  if (!profile) {
    sessionStorage.setItem(REDIRECT_KEY, location.pathname + location.search);
    return <Navigate to="/" replace />;
  }

  return children;
}


// =============================================================================
// REQUIRE FEATURE — gated pages (config.js FEATURE_MIN_TIER)
// =============================================================================
// Sits inside RequireAuth, which already waits for the profile (and so the plan)
// to load — a Pro+ user never sees the prompt flash. Sits OUTSIDE
// AuthenticatedLayout, so the layout still hands its responsive props straight to
// the page. Without access: the normal layout with an empty page and the upgrade
// prompt; "Upgrade" goes to /subscription, anything else (Not now, Escape, a
// click outside) goes to the Library.

// The empty page behind the prompt. A component (not a bare <div>), because
// AppLayout hands its responsive props to its child and a DOM element can't take them.
function EmptyPage() { return null; }

function RequireFeature({ feature, children }) {
  const { profile } = useAuth();
  const navigate = useNavigate();
  if (canUseFeature(feature, profile?.currentTier || 'free')) return children;
  return (
    <>
      <AuthenticatedLayout><EmptyPage /></AuthenticatedLayout>
      <UpgradePrompt open onClose={() => navigate('/library', { replace: true })} />
    </>
  );
}


// =============================================================================
// AUTHENTICATED LAYOUT WRAPPER
// =============================================================================

function AuthenticatedLayout({ children, showConversations = true }) {
  const { logout } = useAuth();

  return (
    <AppLayout onLogout={logout} showConversations={showConversations}>
      {children}
    </AppLayout>
  );
}


// =============================================================================
// APP INNER (uses router hooks)
// =============================================================================

// Speed and subtitles of the profile this session plays as (the access token always carries the
// primary profile). The players read these when a video starts and save changes via PATCH /profile/preferences.
function playbackPreferences(profiles) {
  const p = (profiles || []).find((x) => x && x.isPrimary) || (profiles || [])[0] || {};
  const speed = Number(p.playbackSpeed);
  return {
    preferredPlaybackSpeed: speed > 0 ? speed : 1,
    preferredSubtitles: p.subtitleLanguage ? p.subtitleLanguage : 'off',
  };
}

function AppInner() {
  const navigate = useNavigate();
  const location = useLocation();

  // ---------------------------------------------------------------------------
  // AUTH STATE
  // ---------------------------------------------------------------------------

  // --- AUTH / SESSION (not user-profile data) ---
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [isLoading, setIsLoading] = useState(true);
  const [sessionId, setSessionId] = useState(null);

  // --- SINGLE SOURCE OF TRUTH: the entire user profile in one object ---
  //   { name, email, currentTier, subscription: { ... }, profiles: [],
  //     preferredPlaybackSpeed, preferredSubtitles }   (see playbackPreferences above)
  const [profile, setProfile] = useState(null);

  // --- ALGORITHM + PROGRESS: one combined per-user payload from the Content API ---
  // They travel together (progress is the algorithm's input). `refreshAlgorithm`
  // hydrates BOTH from a single response. progressByVideo is the SOLE home for
  // per-user progress, keyed by video id, merged onto the catalog at render.
  // null / {} until the real payload lands. Every consumer already optional-chains
  // (LibraryPage reads `algorithm?.recentlyWatched || []`, the progress helpers all
  // null-check), so an empty start is safe — and unlike the old placeholder seed it
  // cannot render someone else's data as though it were yours.
  const [algorithm, setAlgorithm] = useState(null);
  const [progressByVideo, setProgressByVideo] = useState({});

  // --- CATALOG: full content tree from the Content API ---
  // Starts EMPTY. It used to start as PLACEHOLDER_CATALOG, which meant the first
  // paint of every page was fabricated content, replaced a moment later — and,
  // far worse, that a failed fetch left the user browsing an invented catalogue
  // with 404s on every click and no error anywhere. catalogLoading drives the
  // loading state; catalogError drives CatalogErrorOverlay.
  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState(false);

  const tokenRef = useRef(token);
  tokenRef.current = token;

  // Derived views of the single `profile` source (used in render + the temp
  // auth-compat scaffold below). These are NOT separate state.
  const currentTier = profile?.currentTier || 'free';
  const colors = TIER_COLORS[currentTier];
  const user = useMemo(
    () => (profile ? { name: profile.name, email: profile.email } : null),
    [profile]
  );

  // ---------------------------------------------------------------------------
  // API CLIENT
  // ---------------------------------------------------------------------------

  const api = useMemo(() => createApiClient({
    getToken: () => tokenRef.current,
    setToken: (newToken) => {
      localStorage.setItem(TOKEN_KEY, newToken);
      setToken(newToken);
      tokenRef.current = newToken;
    },
    onSessionExpired: () => {
      console.warn('Session expired, logging out');
      localStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(REDIRECT_KEY);
      setToken(null);
      setProfile(null);
      setSessionId(null);
      setCatalog([]);
      setAlgorithm(null);
      setProgressByVideo({});
      setCatalogError(false);        // a fresh session starts without an error
    },
  }), []);

  // ---------------------------------------------------------------------------
  // SYNC SUBSCRIPTION — Stripe-verified state from /billing/subscription-status
  // ---------------------------------------------------------------------------

  const syncSubscription = useCallback(async () => {
    try {
      const response = await api.fetch('/billing/subscription-status');
      if (!response.ok) throw new Error('Sync failed');

      const data = await response.json();
      // Stripe is the authoritative writer for the subscription sub-object.
      setProfile((prev) => ({
        ...(prev || {}),
        currentTier: data.currentTier || 'free',
        subscription: {
          ...((prev && prev.subscription) || {}),
          status: data.subscriptionStatus || null,
          cancelAtPeriodEnd: !!data.cancelAtPeriodEnd,
          currentPeriodEnd: data.currentPeriodEnd || null,
          pendingDowngradeTier: data.pendingDowngradeTier || null,
          downgradeDate: data.downgradeDate || null,
        },
      }));

      // C-12 — tier rides in the access token as a snapshot, and Content-API gating
      // reads it from the token. If the Stripe-verified tier differs from what's baked
      // in the current token, force a refresh so the new tier re-bakes immediately
      // (pairs with U-6, the server re-bake). Best-effort: if anything here fails the
      // token still self-corrects on its next natural expiry.
      try {
        if (data.currentTier && api.refreshToken) {
          let tokenTier = null;
          try {
            const seg = tokenRef.current && tokenRef.current.split('.')[1];
            if (seg) {
              tokenTier = JSON.parse(
                atob(seg.replace(/-/g, '+').replace(/_/g, '/'))
              ).tier;
            }
          } catch (e) { /* unreadable token → treat as changed */ }
          if (tokenTier !== data.currentTier) await api.refreshToken();
        }
      } catch (e) {
        /* refresh is best-effort; token re-bakes on next expiry */
      }

      return data;
    } catch (err) {
      console.error('syncSubscription failed:', err);
      return null;
    }
  }, [api]);

  // ---------------------------------------------------------------------------
  // REFRESH ACCOUNT — single source of truth from database
  // ---------------------------------------------------------------------------

  const refreshAccount = useCallback(async () => {
    try {
      const response = await api.fetch('/auth/restore', { method: 'POST' });
      if (!response.ok) throw new Error('Session expired');

      const data = await response.json();
      const updatedAt = data.account.subscriptionUpdatedAt;
      setProfile((prev) => ({
        name: data.account.displayName || data.account.email.split('@')[0],
        email: data.account.email,
        currentTier: data.account.currentTier || 'free',
        subscription: {
          status: data.account.paymentSubscriptionStatus || null,
          cancelAtPeriodEnd: !!data.account.paymentCancelAtPeriodEnd,
          currentPeriodEnd: data.account.paymentCurrentPeriodEnd || null,
          updatedAt: updatedAt || null,
          // preserved until the next Stripe sync overwrites them
          pendingDowngradeTier: (prev && prev.subscription && prev.subscription.pendingDowngradeTier) || null,
          downgradeDate: (prev && prev.subscription && prev.subscription.downgradeDate) || null,
        },
        profiles: data.profiles || [],
        ...playbackPreferences(data.profiles),
      }));
      setSessionId(data.sessionId || null);

      // 45-day check: if billing activity is recent, verify against Stripe.
      // Skip if returning from checkout — the return handler fires sync separately.
      const isRecent = updatedAt && (Date.now() - new Date(updatedAt).getTime()) < SYNC_WINDOW_MS;
      const isReturnFromCheckout = window.location.search.includes('success=true');
      if (isRecent && !isReturnFromCheckout) {
        syncSubscription();
      }

      return data.account.currentTier || 'free';
    } catch (err) {
      console.error('refreshAccount failed:', err);
      return null;
    }
  }, [api, syncSubscription]);

  // ---------------------------------------------------------------------------
  // SESSION VALIDATION
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const validateSession = async () => {
      const storedToken = localStorage.getItem(TOKEN_KEY);

      if (!storedToken) {
        setIsLoading(false);
        return;
      }

      try {
        await refreshAccount();
      } catch (err) {
        console.error('Session validation failed:', err);
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setProfile(null);
      } finally {
        setIsLoading(false);
      }
    };

    validateSession();
  }, [refreshAccount]);

  // ---------------------------------------------------------------------------
  // FETCH COURSES (placeholder — uncomment fetch when API is ready)
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // LOAD CATALOG — full content tree from the Content API (once per session)
  // ---------------------------------------------------------------------------

  // ===========================================================================
  // SIGNED-URL FRESHNESS  ("ensureFresh")
  // ===========================================================================
  // THE BUG THIS FIXES. Every URL the Content API returns carries a prefix-scoped
  // `?t=` HMAC token, and the SAME token is appended to everything under a
  // lesson's prefix — thumbnail, preview and video alike. Its expiry sits in
  // plaintext as the middle segment:
  //
  //   ?t=<base64 prefix>.<unix expiry>.<signature>
  //
  // Video URLs never go stale because /video/:id is fetched fresh at page mount.
  // The catalog and the algorithm feed are fetched ONCE per session and then held
  // in state indefinitely — so their tokens die while the URLs live on, and every
  // thumbnail 403s until a manual page refresh re-runs the fetch. That is the
  // whole bug: the cache is correct, the tokens inside it are not.
  //
  // WHY THERE IS NO TIMER. ensureFresh only runs when something calls it, and the
  // callers are user-driven: tab becomes visible, route changes, pointer/key
  // input. A page left open and untouched on a second monitor fires NONE of these
  // and performs zero network activity. The margin below decides whether a call
  // DOES anything; it never causes one.
  // ===========================================================================

  const tokenExpRef   = useRef(0);    // soonest known expiry, unix seconds
  const marginRef     = useRef(0);    // how early to refresh, seconds
  // Generation counter. ensureFresh bumps it so the FIRST payload of the new
  // round overwrites the expiry rather than min-comparing against the old one.
  // An earlier version zeroed tokenExpRef up front instead — which meant a FAILED
  // refresh left it at 0, isTokenStale() then returned false forever (the
  // fail-closed rule), and the page went permanently quiet. Found in simulation.
  const freshGenRef   = useRef(0);
  const noteGenRef    = useRef(-1);
  const freshBusyRef  = useRef(false);
  const freshTriedRef = useRef(0);    // last attempt, ms — the failed-refetch cooldown
  // Lets the image backstop below attach its window listener ONCE, with [] deps,
  // while still always calling the current ensureFresh.
  const ensureFreshRef = useRef(() => {});

  const FRESH_MAX_MARGIN_S = 60 * 60; // ceiling: refresh up to an hour early
  const FRESH_COOLDOWN_MS  = 60 * 1000;

  // Pull the expiry out of a signed URL. Returns null on ANYTHING unexpected —
  // fail closed. A parse that fell back to 0 would read as "expired" forever and
  // refetch on every click.
  const parseTokenExpiry = useCallback((url) => {
    if (typeof url !== 'string') return null;
    const q = url.indexOf('?t=');
    if (q === -1) return null;
    const raw = url.slice(q + 3).split('&')[0];
    const parts = raw.split('.');
    if (parts.length !== 3) return null;
    const exp = Number(parts[1]);
    return Number.isFinite(exp) && exp > 0 ? exp : null;
  }, []);

  // One token per response, so one URL is enough — no need to walk 162 topics.
  //
  // DEPTH 12, NOT 6. The catalog nests deeper than it looks:
  //   catalog[] 0 → subject 1 → courses[] 2 → course 3 → chapters[] 4 →
  //   chapter 5 → sections[] 6 → section 7 → topics[] 8 → topic 9 → url 10
  // A limit of 6 bailed before ever reaching a topic, so no catalog URL was
  // found, tokenExpRef stayed 0, isTokenStale() returned false, and the whole
  // fix silently did nothing. It only looked alive because the algorithm feed
  // resolves at depth 2 — and that payload is empty for a new user.
  // The limit exists only to bound pathological input; JSON from an API cannot
  // be circular, so 12 is generous rather than tight.
  const findFirstSignedUrl = useCallback((payload) => {
    let found = null;
    const walk = (v, depth) => {
      if (found || depth > 12 || v == null) return;
      if (typeof v === 'string') { if (v.indexOf('?t=') > -1) found = v; return; }
      if (Array.isArray(v)) { for (let i = 0; i < v.length && !found; i++) walk(v[i], depth + 1); return; }
      if (typeof v === 'object') {
        const keys = Object.keys(v);
        for (let i = 0; i < keys.length && !found; i++) walk(v[keys[i]], depth + 1);
      }
    };
    walk(payload, 0);
    return found;
  }, []);

  // Record the soonest expiry seen, and set an ADAPTIVE margin.
  //   margin = min(1 hour, half the lifetime remaining when the payload arrived)
  // Adaptive because we must not assume the Worker's TTL. If tokens ever lived
  // less than an hour, a fixed 1-hour margin would make a FRESHLY fetched payload
  // instantly "stale" and every interaction would refetch forever. Halving the
  // observed lifetime makes that loop structurally impossible and needs no
  // configuration if the TTL changes later.
  const noteTokenExpiry = useCallback((url) => {
    const exp = parseTokenExpiry(url);
    if (!exp) return;
    const nowS = Date.now() / 1000;
    const lifetime = exp - nowS;
    if (lifetime <= 0) return;                       // already dead; nothing to learn
    const margin = Math.min(FRESH_MAX_MARGIN_S, lifetime / 2);
    // First payload of a new generation starts the window; later ones narrow it,
    // because catalog and algorithm are fetched moments apart and their tokens
    // expire moments apart. Track the SOONEST.
    if (noteGenRef.current !== freshGenRef.current) {
      noteGenRef.current = freshGenRef.current;
      tokenExpRef.current = exp;
      marginRef.current = margin;
    } else if (exp < tokenExpRef.current) {
      tokenExpRef.current = exp;
      marginRef.current = margin;
    }
  }, [parseTokenExpiry]);

  const isTokenStale = useCallback(() => {
    if (!tokenExpRef.current) return false;          // nothing parsed → never refresh
    return (tokenExpRef.current - Date.now() / 1000) < marginRef.current;
  }, []);

  // Bumping this re-runs the catalog effect below. The existing fetch is untouched.
  const [catalogNonce, setCatalogNonce] = useState(0);
  // The token a catalog was successfully loaded FOR. Set only on success, so the
  // failure path below can tell "first load failed, we have nothing" (fall back)
  // from "background refresh failed, we still have good data" (keep it).
  const catalogOkForRef = useRef(null);

  // ---------------------------------------------------------------------------

  // `catalogNonce` is the only addition: bumping it from ensureFresh re-runs this
  // effect and re-mints every signed URL in the tree. The fetch itself is unchanged.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    // Raise catalogLoading whenever we do NOT yet hold good data for this token —
    // first load, and every retry after a failure (which is what drives the
    // overlay's "Reconnecting…" state). Do NOT raise it for a background refresh
    // of a catalog we already have, because CoursesPage renders an empty div
    // while it is true and the page would blank mid-session.
    if (catalogOkForRef.current !== token) setCatalogLoading(true);
    api.fetch('/catalog/hierarchy')
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('catalog fetch failed'))))
      .then((body) => {
        if (cancelled) return;
        const data = body.data || [];
        setCatalog(data);
        setCatalogError(false);
        catalogOkForRef.current = token;          // we now hold good data for this token
        noteTokenExpiry(findFirstSignedUrl(data));
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load catalog:', err);
        // CRITICAL — only fall back on the FIRST load of a session.
        // This effect can now re-run mid-session (ensureFresh bumps catalogNonce
        // to re-mint expiring signed URLs). Before that it ran once at login, so
        // this catch only ever fired when there was no catalog to lose. Now a
        // transient failure during a background refresh — one API blip, a moment
        // offline — would REPLACE the user's real, working catalog with
        // fabricated placeholder content: wrong lesson names, dead links, 404s
        // on every click, and no error shown. Keep what we have instead.
        // (Fix 2 removes the placeholder fallback entirely; until then this
        // guard is what stops a background refresh from poisoning good data.)
        // Raise the error ONLY when there is nothing to fall back to. If a
        // BACKGROUND refresh failed (ensureFresh re-minting expiring signed URLs)
        // the user still has a good catalog on screen and must not be interrupted
        // — App renders the overlay on `catalog.length === 0`, so this flag alone
        // is not enough to blank a working app.
        setCatalogError(true);
        // NOTE: no placeholder fallback. A failed fetch used to call
        // setCatalog(PLACEHOLDER_CATALOG), so an outage looked like working
        // software: invented lesson names, 404s on every click, no error shown.
        // Now the catalog stays as it is — empty on a cold failure (overlay
        // appears) or intact on a background failure (silent retry).
      })
      .finally(() => { if (!cancelled) setCatalogLoading(false); });
    return () => { cancelled = true; };
  }, [token, api, catalogNonce, noteTokenExpiry, findFirstSignedUrl]);

  // ---------------------------------------------------------------------------
  // LOAD USER STATE — combined { progressByVideo, algorithm } from the Content API.
  // Starts empty; hydrated by refreshAlgorithm once the endpoint responds.
  // ---------------------------------------------------------------------------

  // Tracks the latest algorithm request so a stale in-flight response (e.g. one that
  // resolves after logout or after a newer refresh) can't overwrite current state.
  const algoReqRef = useRef(0);
  const algoInFlightRef = useRef(null);   // single-flight lock, see refreshAlgorithm

  // ===========================================================================
  // refreshAlgorithm — re-pulls the suggestion model from the user server.
  // Triggers:
  //   1. once on login (the effect below)
  //   2. when the user navigates AWAY from VideoViewPage — that page calls this in an
  //      unmount cleanup, so it only fires after the user was actually on the page, and
  //      it fires on any exit (Library, Home, back button, anywhere). The player
  //      heartbeat streams watch progress to the backend during playback, so by the time
  //      the page tears down the server has already recomputed suggestions; leaving pulls
  //      that fresh result, so Library/Home reflect what was just watched.
  // Note: navigating video -> video does NOT remount the page, so it won't refresh
  //      between consecutive videos by design — only on leaving the player entirely.
  // Consume it in a page via:  const { refreshAlgorithm } = useAuth();
  // Keeps current data on failure; the reqId guard drops any stale in-flight response.
  // ===========================================================================
  // refreshAlgorithm — fetch the COMBINED per-user payload { progressByVideo,
  // algorithm } from the Content API and hydrate both. Progress and algorithm
  // travel together because progress is the algorithm's input. Called once on
  // login (effect below) and on every video-page exit (so Library/Home reflect
  // what was just watched). Keeps current data on failure; the reqId guard drops
  // any stale in-flight response.
  // SINGLE FLIGHT. The lock lives HERE, not in ensureFresh, because
  // refreshAlgorithm has callers that ensureFresh knows nothing about:
  // VideoViewPage fires it on unmount and again on video.id change. Leaving a
  // lesson therefore triggers the page's own call AND the route-change trigger
  // at the same instant — two concurrent /me/state requests, every time.
  //
  // A second caller now receives the IN-FLIGHT PROMISE instead of starting a new
  // request, so concurrency is impossible regardless of who calls. Same pattern
  // api.js already uses for token refresh (see createApiClient → refreshToken).
  const refreshAlgorithm = useCallback(async () => {
    if (!token) return undefined;
    if (algoInFlightRef.current) return algoInFlightRef.current;

    const reqId = ++algoReqRef.current;
    const run = (async () => {
      try {
        // TODO: point at the real combined user-state endpoint when built.
        const res = await api.fetch('/me/state');
        if (!res.ok) return;                                   // keep current on non-2xx
        const body = await res.json();
        const data = body && body.data;
        if (reqId === algoReqRef.current && data) {
          if (data.algorithm) setAlgorithm(data.algorithm);
          if (data.progressByVideo) setProgressByVideo(data.progressByVideo);
          // The algorithm feed carries SIGNED thumbnailUrl / previewUrl too
          // (CONTRACT.md §3), so this response re-mints those URLs as well.
          noteTokenExpiry(findFirstSignedUrl(data.algorithm));
        }
      } catch (e) {
        /* keep current progress + algorithm (placeholder or last good) on failure */
      } finally {
        algoInFlightRef.current = null;
      }
    })();

    algoInFlightRef.current = run;
    return run;
  }, [token, api, noteTokenExpiry, findFirstSignedUrl]);

  useEffect(() => {
    refreshAlgorithm();
    return () => {
      algoReqRef.current += 1;        // invalidate any in-flight response
      algoInFlightRef.current = null; // and drop the lock, or a NEW session could be
                                      // handed the PREVIOUS session's promise and
                                      // never fetch its own state
    };
  }, [refreshAlgorithm]);

  // ensureFresh — the single entry point. Four gates, cheapest first, so the
  // overwhelming majority of calls (every click, every route change) cost one
  // subtraction and return.
  //
  //   1. in flight   — a refresh is already running
  //   2. cooldown    — guards the FAILED refresh, where expiry never improves and
  //                    every subsequent trigger would otherwise retry immediately
  //   3. staleness   — the real gate; rejects ~99% of calls
  //   4. otherwise   — refresh BOTH signed payloads together, one token, one moment
  //
  // `{ force: true }` skips ONLY the staleness gate. The in-flight lock and the
  // cooldown still apply. Forced calls come from the image backstop below, where
  // a 403'd thumbnail is EVIDENCE the token is dead now — as opposed to the
  // staleness check, which is a prediction. Evidence beats prediction: clock
  // skew, a shorter-than-advertised TTL, or a Worker-side change would all leave
  // isTokenStale() saying "fine" while the URLs are already dead.
  const ensureFresh = useCallback((opts) => {
    const force = !!(opts && opts.force === true);
    if (!token) return;
    if (freshBusyRef.current) return;
    if (Date.now() - freshTriedRef.current < FRESH_COOLDOWN_MS) return;
    if (!force && !isTokenStale()) return;

    freshBusyRef.current = true;
    freshTriedRef.current = Date.now();
    freshGenRef.current += 1;                // new window; do NOT clear the old expiry

    setCatalogNonce((n) => n + 1);           // re-mints catalog URLs
    Promise.resolve(refreshAlgorithm())      // re-mints algorithm-feed URLs
      .catch(() => {})
      .finally(() => { freshBusyRef.current = false; });
  }, [token, isTokenStale, refreshAlgorithm]);

  // Keep the ref current so the [] -deps listener above always calls the latest
  // ensureFresh without re-attaching on every identity change.
  useEffect(() => { ensureFreshRef.current = ensureFresh; }, [ensureFresh]);

  // ── TRIGGERS ───────────────────────────────────────────────────────────────
  // All three are universally supported and all three are user-driven.

  // 1. Tab becomes visible — backgrounded tab, locked phone, app switch.
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible') ensureFresh(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [ensureFresh]);

  // 2. Route change — in-app navigation after an idle spell.
  useEffect(() => { ensureFresh(); }, [location.pathname, ensureFresh]);

  // 3. Interaction — catches the case the other two miss entirely: a tab left
  //    OPEN AND VISIBLE but untouched for hours. Passive + capture, and the
  //    handler exits on its first gate, so this is not a measurable cost.
  useEffect(() => {
    const onAct = () => ensureFresh();
    const opts = { passive: true, capture: true };
    document.addEventListener('pointerdown', onAct, opts);
    document.addEventListener('keydown', onAct, opts);
    return () => {
      document.removeEventListener('pointerdown', onAct, opts);
      document.removeEventListener('keydown', onAct, opts);
    };
  }, [ensureFresh]);

  // 4. IMAGE BACKSTOP — the only trigger that reacts to the FAILURE rather than
  //    predicting it. A thumbnail that 403s proves the token is dead now, even if
  //    the staleness clock disagrees, so this forces a refresh.
  //
  //    ONE GLOBAL LISTENER, not an onError prop on each <img>. There are five
  //    render sites today (LibraryPage x2, SearchBar, SearchResultsPage x2) and
  //    any new one would silently miss out. Image load errors do NOT bubble, but
  //    they DO fire on window during the CAPTURE phase — hence `true`.
  //
  //    Three guards, because onError fires for every kind of image failure:
  //      a. only IMG elements whose src carries `?t=` — an unsigned or genuinely
  //         missing image cannot be fixed by re-minting a token, so it must not
  //         trigger one
  //      b. one attempt per URL, ever — a permanently 404ing thumbnail would
  //         otherwise retry for as long as it stays on screen
  //      c. a session cap — if three refreshes have not fixed it, this is not a
  //         token problem and we should stop asking
  //    ensureFresh's own in-flight lock and 60s cooldown collapse the burst you
  //    get when the whole page's images fail at once (i.e. offline).
  useEffect(() => {
    const tried = new Set();
    let forced = 0;
    const MAX_FORCED = 3;

    const onResourceError = (e) => {
      const el = e && e.target;
      if (!el || el.tagName !== 'IMG') return;
      const src = el.currentSrc || el.src || '';
      if (src.indexOf('?t=') === -1) return;      // not a signed URL — not our problem
      if (tried.has(src)) return;                 // this exact URL already had its shot
      tried.add(src);
      if (forced >= MAX_FORCED) return;
      forced += 1;
      ensureFreshRef.current({ force: true });
    };

    window.addEventListener('error', onResourceError, true);   // capture: img errors don't bubble
    return () => window.removeEventListener('error', onResourceError, true);
  }, []);


  // ---------------------------------------------------------------------------
  // reportProgress — the frontend's ONLY write. A throttled, fire-and-forget
  // heartbeat from each player (lesson + answer-key video, keyed by their own ids).
  // It optimistically updates the local progress map so bars + resume reflect
  // watching in-session; the server is the source of truth on the next
  // refreshAlgorithm pull. Completion is sticky (never un-completes on scrub-back).
  // ---------------------------------------------------------------------------
  // `played` (optional): seconds actually played since the last heartbeat, for the viewing counter
  // (useHeartbeat). Sent only when there are some; it doesn't change anything here.
  // `view` (optional): true once per viewing session (useHeartbeat). Sent only when true.
  const reportProgress = useCallback((videoId, positionSeconds, durationSeconds, played, view) => {
    if (!videoId) return;
    setProgressByVideo((prev) => {
      const prevEntry = prev[videoId];
      const completed = (durationSeconds > 0 && positionSeconds / durationSeconds >= 0.95)
        || !!(prevEntry && prevEntry.completed);
      return { ...prev, [videoId]: { positionSeconds, durationSeconds, completed } };
    });
    // C-8: fire-and-forget heartbeat to the durable store. Optimistic local update
    // above already moved the bars/resume; this persists it. Errors are swallowed —
    // a dropped heartbeat just means the next /me/state pull is the source of truth.
    api.fetch('/progress', {
      method: 'POST',
      body: JSON.stringify({
        videoId, positionSeconds, durationSeconds,
        ...(Array.isArray(played) && played.length ? { played } : {}),
        ...(view === true ? { view: true } : {}),
      }),
    }).catch(() => {});
  }, [api]);

  // ---------------------------------------------------------------------------
  // LOGIN HANDLER (email/password)
  // ---------------------------------------------------------------------------

  const login = useCallback(async (jwt) => {
    localStorage.setItem(TOKEN_KEY, jwt);
    setToken(jwt);
    tokenRef.current = jwt;

    try {
      await refreshAccount();

      const redirectPath = sessionStorage.getItem(REDIRECT_KEY);
      sessionStorage.removeItem(REDIRECT_KEY);
      navigate(redirectPath || '/', { replace: true });
    } catch (err) {
      console.error('Login failed:', err);
      localStorage.removeItem(TOKEN_KEY);
      setToken(null);
      tokenRef.current = null;
      setProfile(null);
    }
  }, [navigate, refreshAccount]);

  // ---------------------------------------------------------------------------
  // LOGIN WITH FULL RESPONSE (Google/Apple)
  // ---------------------------------------------------------------------------

  const loginWithResponse = useCallback((authResponse) => {
    const { accessToken, account, profiles: responseProfiles, sessionId: sid } = authResponse;

    localStorage.setItem(TOKEN_KEY, accessToken);
    setToken(accessToken);
    tokenRef.current = accessToken;

    setProfile({
      name: account.displayName || account.email.split('@')[0],
      email: account.email,
      currentTier: account.currentTier || 'free',
      subscription: {
        status: account.paymentSubscriptionStatus || null,
        cancelAtPeriodEnd: !!account.paymentCancelAtPeriodEnd,
        currentPeriodEnd: account.paymentCurrentPeriodEnd || null,
        updatedAt: account.subscriptionUpdatedAt || null,
        pendingDowngradeTier: null,
        downgradeDate: null,
      },
      profiles: responseProfiles || [],
      ...playbackPreferences(responseProfiles),
    });
    setSessionId(sid || null);

    const redirectPath = sessionStorage.getItem(REDIRECT_KEY);
    sessionStorage.removeItem(REDIRECT_KEY);
    navigate(redirectPath || '/', { replace: true });
  }, [navigate]);

  // ---------------------------------------------------------------------------
  // LOGOUT
  // ---------------------------------------------------------------------------

  const logout = useCallback(async () => {
    const currentToken = tokenRef.current;
    const currentSessionId = sessionId;

    try {
      if (currentToken) {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 3000);

        await fetch(`${API_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${currentToken}`,
          },
          credentials: 'include',
          body: JSON.stringify({ sessionId: currentSessionId }),
          signal: controller.signal,
        });

        clearTimeout(timeout);
      }
    } catch (err) {
      console.error('Backend logout failed:', err);
    } finally {
      localStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(REDIRECT_KEY);
      setToken(null);
      tokenRef.current = null;
      setProfile(null);
      setSessionId(null);
      setCatalog([]);
      setAlgorithm(null);
      setProgressByVideo({});
      setCatalogError(false);        // a fresh session starts without an error
      navigate('/', { replace: true });
    }
  }, [navigate, sessionId]);

  // ---------------------------------------------------------------------------
  // STRIPE RETURN HANDLER — fires syncSubscription after checkout
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('success') !== 'true') return;

    // Clean URL immediately
    navigate(location.pathname, { replace: true });

    // Verify subscription state directly from Stripe
    syncSubscription();
  }, [location.search, location.pathname, navigate, syncSubscription]);

  // ---------------------------------------------------------------------------
  // AUTH CONTEXT VALUE
  // ---------------------------------------------------------------------------

  const authValue = useMemo(() => ({
    // --- single source of truth ---
    profile, algorithm, catalog, catalogLoading,
    // --- per-user progress (sole home for progress; merged onto catalog at render) ---
    progressByVideo,
    getProgress: (videoId) => (videoId && progressByVideo[videoId]) || null,
    reportProgress,
    // --- auth / session ---
    token, isLoading, sessionId,
    login, loginWithResponse, logout, refreshAccount, refreshAlgorithm, ensureFresh, syncSubscription,
    API_URL, api,
  }), [profile, algorithm, catalog, catalogLoading, progressByVideo, reportProgress, token, isLoading, sessionId, login, loginWithResponse, logout, refreshAccount, refreshAlgorithm, ensureFresh, syncSubscription, api]);

  // ---------------------------------------------------------------------------
  // FULLSCREEN STATE TRACKING (for landscape overlay exception)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const onFullscreenChange = () => {
      if (document.fullscreenElement || document.webkitFullscreenElement) {
        document.body.setAttribute('data-fullscreen', 'true');
      } else {
        document.body.removeAttribute('data-fullscreen');
      }
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
    };
  }, []);

  // ---------------------------------------------------------------------------
  // PHONE HELD SIDEWAYS (for the landscape overlay)
  // ---------------------------------------------------------------------------
  // Read the PHYSICAL orientation and the PHYSICAL screen size, not the viewport:
  // the on-screen keyboard shrinks the viewport until a portrait phone reads as
  // "landscape" to a media query, which put the "Rotate phone" overlay over the
  // login form. screen.orientation (iOS 16.4+, Chrome 38+) and the legacy
  // window.orientation fallback report rotation only; screen.width/height give
  // the device's short side (<= 500 on every phone, >= 600 on every tablet).
  // If neither API exists the attribute is never set and the overlay never shows.
  useEffect(() => {
    const isPhone = () => Math.min(window.screen.width, window.screen.height) <= 500
      && window.matchMedia('(hover: none) and (pointer: coarse)').matches;
    const isLandscape = () => (window.screen.orientation
      ? String(window.screen.orientation.type).startsWith('landscape')
      : Math.abs(Number(window.orientation) || 0) === 90);
    const update = () => document.body.toggleAttribute('data-phone-landscape', isPhone() && isLandscape());
    update();
    const target = window.screen.orientation || window;
    const event = window.screen.orientation ? 'change' : 'orientationchange';
    target.addEventListener(event, update);
    return () => { target.removeEventListener(event, update); document.body.removeAttribute('data-phone-landscape'); };
  }, []);

  // ---------------------------------------------------------------------------
  // LOADING SCREEN
  // ---------------------------------------------------------------------------

  if (isLoading) {
    // DARK, deliberately. This paints before the session has been validated, so
    // the tier is unknown — there is nothing to theme against. It uses the
    // pre-auth palette because that is what first paint is: index.html sets the
    // same ink on <body>, so the page comes up dark and stays dark through
    // mount. A logged-out visitor and a Pro subscriber both see one continuous
    // surface. A free-tier user gets one transition, ink to parchment, once
    // their tier is known — which is strictly better than the white flash every
    // visitor used to get.
    return (
      <div
        className="rb-public"
        style={{
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
        }}
      >
        <PublicTheme />

        <PublicMark size="lg" style={{ marginBottom: 'var(--s-5)' }} />

        <div
          className="rb-spinner"
          style={{ width: '32px', height: '32px' }}
        />
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // RENDER ROUTES
  // ---------------------------------------------------------------------------

  return (
    <AuthContext.Provider value={authValue}>
      <ThemeProvider tier={currentTier}>
      <div style={{ minHeight: '100vh', background: colors.bg }}>
        <ScrollToTop />

        {/* CATALOG FAILURE — total block, above everything.
            Conditions, all three required:
              token          — never show this to a logged-out visitor, and never
                               flash it during logout when catalog resets to []
              catalogError   — the last fetch actually failed
              catalog empty  — and we have NOTHING to fall back to. A BACKGROUND
                               refresh failure (ensureFresh re-minting expiring
                               signed URLs) leaves a good catalog on screen, and
                               must not interrupt the user. That is why this tests
                               the data, not just the flag.
            Replaces setCatalog(PLACEHOLDER_CATALOG), which let an outage look
            like working software. Temporary by design — see the component. */}
        {token && catalogError && catalog.length === 0 && (
          <CatalogErrorOverlay
            retrying={catalogLoading}
            onRetry={() => setCatalogNonce((n) => n + 1)}
          />
        )}

        {/* Landscape overlay — always in DOM, shown/hidden purely by CSS */}
        <div className="portrait-overlay">
          <div className="portrait-overlay-inner">
            <div className="portrait-overlay-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#C9A962" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                <rect x="5" y="1" width="14" height="22" rx="2.5" />
                <path d="M12 18h.01" strokeWidth="1.5" />
              </svg>
            </div>
            <div className="portrait-overlay-line" />
            <p className="portrait-overlay-text">Rotate phone to ensure best experience.</p>
          </div>
        </div>

        <Routes>
          {/* PUBLIC ROUTES */}
          <Route path="/" element={
            user ? (
              <AuthenticatedLayout>
                <HomePage />
              </AuthenticatedLayout>
            ) : (
              <AuthUI />
            )
          } />
          {/* AD LANDING PAGE — public, reachable logged in or out.
              Deliberately NOT at "/": that would change what existing users see
              when they open the app. This is a destination for paid traffic, so
              it can be pointed at, measured, and swapped without touching the
              signed-in experience.
              onGetStarted parks /subscription in REDIRECT_KEY, so a visitor who
              picks a plan is taken there automatically once they have signed up. */}
          <Route path="/landingpage" element={
            <LandingPageObs
              onGetStarted={() => {
                sessionStorage.setItem(REDIRECT_KEY, '/subscription');
                navigate('/');
              }}
              onSignIn={() => navigate('/')}
            />
          } />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* PROTECTED — SIDEBAR LAYOUT */}
          <Route path="/library" element={
            <RequireAuth><AuthenticatedLayout>
              <LibraryPage />
            </AuthenticatedLayout></RequireAuth>
          } />
          <Route path="/courses/*" element={
            <RequireAuth><AuthenticatedLayout>
              <CoursesPage />
            </AuthenticatedLayout></RequireAuth>
          } />
          <Route path="/calendar" element={
            <RequireAuth><AuthenticatedLayout>
              <CalendarPage />
            </AuthenticatedLayout></RequireAuth>
          } />
          <Route path="/grade-check" element={
            <RequireAuth><AuthenticatedLayout>
              <GradeCheckPage />
            </AuthenticatedLayout></RequireAuth>
          } />
          <Route path="/drive" element={
            <RequireAuth><AuthenticatedLayout>
              <DrivePage />
            </AuthenticatedLayout></RequireAuth>
          } />
          {/* PLAYLISTS — all four pages share the 'playlists' gate.
              React Router v6 ranks routes by specificity, so "/playlist/:code/edit"
              wins over "/playlist/:code/:item" regardless of order. */}
          <Route path="/playlist" element={
            <RequireAuth><RequireFeature feature="playlists"><AuthenticatedLayout>
              <PlaylistsHomePage />
            </AuthenticatedLayout></RequireFeature></RequireAuth>
          } />
          <Route path="/playlist/:code" element={
            <RequireAuth><RequireFeature feature="playlists"><AuthenticatedLayout>
              <PlaylistPage />
            </AuthenticatedLayout></RequireFeature></RequireAuth>
          } />
          <Route path="/playlist/:code/edit" element={
            <RequireAuth><RequireFeature feature="playlists"><AuthenticatedLayout>
              <PlaylistEditPage />
            </AuthenticatedLayout></RequireFeature></RequireAuth>
          } />
          <Route path="/playlist/:code/:item" element={
            <RequireAuth><RequireFeature feature="playlists"><AuthenticatedLayout>
              <PlaylistPlayPage />
            </AuthenticatedLayout></RequireFeature></RequireAuth>
          } />
          {/* The old address keeps working. */}
          <Route path="/playlists" element={<Navigate to="/playlist" replace />} />
          <Route path="/tools" element={
            <RequireAuth><RequireFeature feature="tools"><AuthenticatedLayout>
              <ToolsPage />
            </AuthenticatedLayout></RequireFeature></RequireAuth>
          } />
          <Route path="/video/:id" element={
            <RequireAuth><AuthenticatedLayout>
              <VideoViewRouter />
            </AuthenticatedLayout></RequireAuth>
          } />
          <Route path="/search" element={
            <RequireAuth><AuthenticatedLayout>
              <SearchResultsPage />
            </AuthenticatedLayout></RequireAuth>
          } />

          {/* PROTECTED — STANDALONE (no sidebar) */}
          <Route path="/subscription" element={
            <RequireAuth>
              <ManageSubscription />
            </RequireAuth>
          } />
          <Route path="/profile" element={
            <RequireAuth><ProfilePage /></RequireAuth>
          } />

          {/* CATCH-ALL */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        {/* GLOBAL STYLES */}
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,400&family=DM+Sans:wght@400;500;600&display=swap');
          
          html, body {
            margin: 0; padding: 0; width: 100%; height: 100%;
            overflow-x: hidden; background: ${colors.bg};
            transition: background 0.6s cubic-bezier(0.4, 0, 0.2, 1);

            /* WHY PULL-TO-REFRESH IS DISABLED.
               On iOS the overscroll gesture moves the VISUAL viewport while
               position:fixed elements stay pinned to the LAYOUT viewport. Abort
               the pull partway — a flick that never commits to a reload — and
               the two stay out of sync: a strip of page background appears at
               the top, and the sidebar toggle, the only fixed interactive
               element in the app, ends up above the visible area. It looks
               present because the paint is stale, but taps land where the
               browser thinks it is, which is off-screen. Every other control
               keeps working because everything else is in normal flow.

               There is no way to force the gesture to complete; the browser
               owns it. Declining it entirely is the only reliable fix, and it
               matches how most app-like sites behave. */
            overscroll-behavior-y: contain;
          }
          /* <body> is the element that scrolls here (html and body are both 100% tall
             with overflow-x hidden). Reserve its scrollbar space permanently so the
             page width never changes when a page becomes taller or shorter than the
             window. On <body> only: on <html> it would reserve a second, empty gutter. */
          body { scrollbar-gutter: stable; }
          /* A focused field scrolled into view stays clear of the fixed wordmark bar on the public pages. */
          html { scroll-padding-top: 90px; }
          #root { min-height: 100%; width: 100%; }
          
          @keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes scaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
          @keyframes spin { to { transform: rotate(360deg); } }
          
          * { box-sizing: border-box; margin: 0; padding: 0; -webkit-tap-highlight-color: transparent; }
          html { scroll-behavior: auto; }
          
          ::-webkit-scrollbar { width: 6px; height: 6px; }
          ::-webkit-scrollbar-track { background: transparent; }
          ::-webkit-scrollbar-thumb { background: rgba(128,128,128,0.3); border-radius: 3px; }
          ::-webkit-scrollbar-thumb:hover { background: rgba(128,128,128,0.5); }
          
          .video-row-scroll::-webkit-scrollbar,
          .collection-videos::-webkit-scrollbar,
          .category-pills::-webkit-scrollbar { height: 0; width: 0; display: none; }
          
          @media (hover: none) and (pointer: coarse) {
            button:hover { opacity: 1; }
            .scroll-arrow { display: none !important; }
          }
          
          /* ---- Landscape overlay (CSS-only, zero JS) ---- */
          .portrait-overlay {
            display: none;
            position: fixed;
            inset: 0;
            z-index: 999999;
            background: #0A0A0A;
            align-items: center;
            justify-content: center;
          }
          .portrait-overlay-inner {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 20px;
            opacity: 0;
            transform: translateY(8px);
            animation: overlayFadeIn 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.1s forwards;
          }
          .portrait-overlay-icon {
            animation: overlayTilt 3s ease-in-out infinite;
          }
          .portrait-overlay-line {
            width: 40px;
            height: 1px;
            background: linear-gradient(90deg, transparent, #C9A962, transparent);
            opacity: 0.5;
          }
          .portrait-overlay-text {
            font-family: "Cormorant Garamond", Georgia, serif;
            font-size: 17px;
            font-weight: 300;
            letter-spacing: 0.03em;
            color: rgba(245, 245, 240, 0.7);
            text-align: center;
            line-height: 1.5;
            margin: 0;
            padding: 0 32px;
          }
          @keyframes overlayFadeIn {
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes overlayTilt {
            0%, 100% { transform: rotate(0deg); }
            20% { transform: rotate(90deg); }
            40% { transform: rotate(90deg); }
            60% { transform: rotate(0deg); }
          }
          .video-landscape-bg {
            display: none;
          }
          
          /* Shown by the PHONE HELD SIDEWAYS effect above (physical orientation), not by a media query. */
          body[data-phone-landscape]:not([data-fullscreen]):not([data-video-page]) .portrait-overlay {
            display: flex;
          }
          @media screen and (orientation: landscape) and (hover: none) and (pointer: coarse) and (max-height: 500px) {
            html, body:not([data-fullscreen]) {
              background: #0A0A0A !important;
            }
            body[data-video-page]:not([data-fullscreen]) .video-landscape-bg {
              display: block;
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              width: 100vw;
              height: 100vh;
              background: #0A0A0A;
              z-index: -1;
            }
            body[data-video-page]:not([data-fullscreen]) .video-player-container {
              position: fixed !important;
              top: 50% !important;
              top: 50svh !important;
              left: 50% !important;
              right: auto !important;
              bottom: auto !important;
              transform: translate(-50%, -50%) !important;
              height: 84vh !important;
              height: 84svh !important;
              width: calc(84vh * 16 / 9) !important;
              width: calc(84svh * 16 / 9) !important;
              max-width: 96vw !important;
              aspect-ratio: unset !important;
              overflow: visible !important;
              z-index: 99999 !important;
              border-radius: 12px !important;
              box-shadow: 0 0 60px rgba(0,0,0,0.8) !important;
            }
          }
        `}</style>
      </div>
      </ThemeProvider>
    </AuthContext.Provider>
  );
}


// =============================================================================
// MAIN APP COMPONENT
// =============================================================================

export default function App() {
  return (
    <BrowserRouter>
      <PlaybackCapabilityProvider>
        {/* Browser-admission check. Sits ABOVE AppInner so a browser with no DRM
            engine never mounts the app at all — no session validation, no catalog
            fetch. See UnsupportedBrowserGate for the full reasoning. */}
        <UnsupportedBrowserGate>
          <AppInner />
        </UnsupportedBrowserGate>
      </PlaybackCapabilityProvider>
    </BrowserRouter>
  );
}
